import {
  Box,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import CardLink from "@pathwright/ux/src/components/card/CardLink"
import SwitchField from "@pathwright/ux/src/components/form/SwitchField"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { Field, Form, Formik, FormikHelpers } from "formik"
import moment from "moment"
import { ChangeEvent } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  NotificationDigestInterval,
  useUpdateNotificationDigestIntervalMutation,
  useUpdateUserMembershipMutation
} from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"

const schema = z.object({
  consentsToCurrent: z.boolean(),
  consentsToPrev: z.boolean().optional(),
  notificationEmailSummary: z.nativeEnum(NotificationDigestInterval)
})

const validate = toFormikValidate(schema)

type Values = z.infer<typeof schema>

// Handles the logic for updating the user's consent to notifications.
function useUpdateConsentToNotifications() {
  const { me, school } = usePathwrightContext()
  const { consent_snapshot, consent_dtime } = me?.membership || {}

  const schoolConsentDescription = school.consent_description || ""
  const consents = !!consent_snapshot
  const consentsToCurrent =
    consents && schoolConsentDescription === consent_snapshot
  const consentsToPrev = consents && !consentsToCurrent

  const [mutation] = useUpdateUserMembershipMutation({})

  function updateConsentToNotifications(consents: boolean) {
    const consent_snapshot = consents ? schoolConsentDescription : null

    const consent_dtime = consents ? new Date().toISOString() : null

    return mutation({
      variables: {
        consents
      },
      optimisticResponse: {
        updateMemberConsent: {
          id: me.id,
          consent_snapshot,
          consent_dtime,
          __typename: "Membership"
        }
      }
    })
  }

  return {
    updateConsentToNotifications,
    consentsToCurrent,
    consentsToPrev,
    schoolConsentDescription,
    consentSnapshot: consent_snapshot || "",
    consentDtime: consent_dtime
  }
}

// Form for updating user's notification preferences.
function UserAugmentationNotificationsSettings() {
  const { me } = usePathwrightContext()

  const {
    updateConsentToNotifications,
    consentsToCurrent,
    consentsToPrev,
    schoolConsentDescription,
    consentSnapshot,
    consentDtime
  } = useUpdateConsentToNotifications()

  const [updateNotificationDigestInterval] =
    useUpdateNotificationDigestIntervalMutation()

  const notificationEmailSummary =
    me?.profile?.notification_digest_interval ||
    NotificationDigestInterval.Never

  const initialValues: Values = {
    consentsToCurrent,
    consentsToPrev,
    notificationEmailSummary
  }

  // NOTE: each field submits the form, but we only handle calling mutations per field that changed.
  // We could refactor to rather call the mutaiton on field change, but that's likely less appropriate(?).
  async function handleSubmit(
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) {
    // Update user's notification email summary (if changed).
    if (
      values.notificationEmailSummary !== initialValues.notificationEmailSummary
    ) {
      return await updateNotificationDigestInterval({
        variables: {
          interval: values.notificationEmailSummary
        },
        optimisticResponse: {
          updateNotificationDigestInterval: {
            id: me.profile?.id,
            notification_digest_interval: values.notificationEmailSummary
          }
        }
      })
    }

    // If either consent field has changed, update the notification consent.
    // The prev consent will only ever be false (since it will only be included if changed).
    // The current consent will either be true or false.
    if (
      values.consentsToCurrent !== initialValues.consentsToCurrent ||
      values.consentsToPrev !== initialValues.consentsToPrev
    ) {
      return await updateConsentToNotifications(
        values.consentsToCurrent || !!values.consentsToPrev
      )
    }
  }

  const disclosure = useDisclosure()
  const { getDisclosureProps, isOpen } = disclosure

  return (
    <VStack w="100%" spacing={3}>
      <CardLink
        icon="bell-fill"
        label="Change notification settings"
        disclosure={disclosure}
        bg={isOpen ? "blackAlpha.200" : undefined}
      />
      <Box w="100%" {...getDisclosureProps()}>
        <Collapse in={isOpen} animateOpacity>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validate={validate}
            enableReinitialize
          >
            {(form) => (
              <Form>
                <VStack
                  align="start"
                  gap={0}
                  w="100%"
                  pl="2"
                  mb="2"
                  borderLeft="1px solid"
                  borderColor="blackAlpha.200"
                >
                  <FormLabel
                    key="notificationEmailSummary"
                    htmlFor="notificationEmailSummary"
                    m={0}
                    w="100%"
                    cursor="pointer"
                    borderRadius="20px"
                    _hover={{
                      textDecoration: "none",
                      bg: "blackAlpha.200"
                    }}
                    p={2}
                  >
                    <FormControl
                      as={HStack}
                      align="center"
                      justify="space-between"
                      w="100%"
                      color="gray.500"
                    >
                      <Text fontSize="xs" my={0} fontWeight="normal">
                        Get notifications email summary
                      </Text>
                      <Field
                        name="notificationEmailSummary"
                        id="notificationEmailSummary"
                        as={Select}
                        children={[
                          <option
                            key="never"
                            value={NotificationDigestInterval.Never}
                          >
                            Never
                          </option>,
                          <option
                            key="daily"
                            value={NotificationDigestInterval.Daily}
                          >
                            Daily
                          </option>,
                          <option
                            key="weekly"
                            value={NotificationDigestInterval.Weekly}
                          >
                            Weekly
                          </option>
                        ]}
                        onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                          await form.setFieldValue(
                            "notificationEmailSummary",
                            e.target.value
                          )
                          await form.submitForm()
                        }}
                        variant="outline"
                        flexBasis="content"
                        color="gray.600"
                        icon={<Pathicon icon="chevron-down" />}
                        size="sm"
                      />
                    </FormControl>
                  </FormLabel>

                  {!!schoolConsentDescription && (
                    <SwitchField
                      option={{
                        field: "consentsToCurrent",
                        label: schoolConsentDescription
                      }}
                    />
                  )}
                  {consentsToPrev && (
                    <VStack w="100%" align="start" spacing={0}>
                      <SwitchField
                        option={{
                          field: "consentsToPrev",
                          label: consentSnapshot
                        }}
                      />
                      {!!consentDtime && (
                        <Text fontSize="xs" px={2} pb={2} opacity={0.6}>
                          {moment(consentDtime).fromNow()}
                        </Text>
                      )}
                    </VStack>
                  )}
                </VStack>
              </Form>
            )}
          </Formik>
        </Collapse>
      </Box>
    </VStack>
  )
}

export default UserAugmentationNotificationsSettings
